import { useState } from 'react'
import { fbt } from 'fbtee'
import { Flag } from 'lucide-react'
import { useAuth } from '../hooks/useAuth'
import { useGuestGuard } from '../hooks/useGuestGuard'
import { useSetReports } from '../hooks/useSetReports'
import { setReportCategoryLabel } from '../lib/setReportLabels'
import { reportOtherNotePlaceholder, reportSentToast } from '../lib/commonStrings'
import { ProblemReportDialog } from './ProblemReportDialog'
import { SET_REPORT_CATEGORIES, type SetReportCategory } from '../types'

interface Props {
  set: { id: string; eventId: string; artistIds: string[] }
  /** The set's name as shown in the dialog title, for the report dialog's description. */
  setName: string
}

/** "Report a problem" link for a set, and the category picker it opens. */
export function SetReportButton({ set, setName }: Props) {
  const { signedInUid } = useAuth()
  const { guard } = useGuestGuard()
  const { submitReport, pendingCategories } = useSetReports(set, signedInUid)
  const [open, setOpen] = useState(false)

  // An artist-less slot has no photo or bio of its own to get wrong.
  const categories = set.artistIds.length > 0
    ? SET_REPORT_CATEGORIES
    : SET_REPORT_CATEGORIES.filter(c => c !== 'image' && c !== 'description')

  const openDialog = guard(() => setOpen(true), {
    intent: 'set_report',
    title: fbt('Sign up to report a problem', 'Guest prompt title when reporting a problem with a set'),
    description: fbt(
      'Create a free account to flag wrong set times, artists or photos so we can fix them.',
      'Guest prompt description when reporting a problem with a set',
    ),
  })

  return (
    <>
      <button
        type="button"
        onClick={openDialog}
        data-testid="set-report-open"
        className="flex items-center gap-1.5 text-xs text-muted-foreground hover:text-foreground cursor-pointer"
      >
        <Flag className="size-3.5" aria-hidden />
        {fbt('Report a problem', 'Link at the bottom of a set dialog to report wrong set data')}
      </button>

      <ProblemReportDialog<SetReportCategory>
        open={open}
        onOpenChange={setOpen}
        description={
          <fbt desc="Description in the set report dialog; setName is the artist or set title">
            About <fbt:param name="setName">{setName}</fbt:param>. We'll check what you flag against the festival's source.
          </fbt>
        }
        categories={categories}
        categoryLabel={setReportCategoryLabel}
        pendingCategories={pendingCategories}
        noteRequired={c => c === 'other'}
        notePlaceholder={c =>
          c === 'other'
            ? reportOtherNotePlaceholder()
            : fbt('Details, e.g. the right time (optional)', 'Placeholder for the optional note on a set report')
        }
        onSubmit={(category, message) => submitReport(category, message)}
        successToast={reportSentToast()}
      />
    </>
  )
}
